import React, {useState} from 'react';
import PropTypes from 'prop-types';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';


import translate from '../../misc/applyTranslation';
import Header from "./Header";
import ContentContainer from "./ContentContainer";
import Info from "./Info";
import Scheduled from "./Scheduled";
import OpenRuns from "./OpenRuns";

const formatClockTime = (value) => {
  if (!value) return '';
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatRelativeSince = (value) => {
  if (!value) return '';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return '';

  const diffSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));

  if (diffSeconds < 60) return `${diffSeconds} sek sedan`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} min sedan`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)} tim sedan`;
  return `${Math.floor(diffSeconds / 86400)} d sedan`;
};

const formatRunningSince = (value) => {
  if (!value) return 'pagar sedan 0 sek';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return 'pagar';

  const diffSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  return `pagar sedan ${diffSeconds} sek`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const isSuccessStatus = (statusCode) => String(statusCode) === '200';

const N8nDashboard = props => {
  const {
    classes,
    n8nSections,
    failedJobs,
    latestFailure,
    refreshN8n,
    lastUpdateTime,
  } = props;

  const [openRunsExpanded, setOpenRunsExpanded] = useState(false);

  const openRunsCount = n8nSections.openRuns?.length || 0;
  const hasFailedJobs = failedJobs.length > 0;
  const hasLongFailureMessage = String(latestFailure?.statusMessage || '').length > 180;

  const getRowStatusClass = (statusCode) => (isSuccessStatus(statusCode) ? classes.n8nSuccessRow : classes.n8nErrorRow);
  const getPillStatusClass = (statusCode) => (isSuccessStatus(statusCode) ? classes.n8nPillSuccess : classes.n8nPillError);
  const getStatusDotColor = (statusCode) => (isSuccessStatus(statusCode) ? '#16a34a' : '#dc2626');

  const renderLastSeenRow = (item, index) => (
    <div key={item.run_id || index} className={`${classes.n8nJobRow} ${getRowStatusClass(item.status_code)}`}>
      <div className={classes.n8nMainCol}>
        <span className={classes.n8nStatusDot} style={{backgroundColor: getStatusDotColor(item.status_code)}}/>
        <div>
          <p className={classes.n8nJobName}>{(item.job || translate('n8n', 'unknown_label') ).toUpperCase()}</p>
          <p className={classes.n8nMeta}>{item.run_id ? `Run ID: ${item.run_id}` : 'Run ID: -'}</p>
        </div>
      </div>

      <div className={classes.n8nTimeCol}>
        <p className={classes.n8nTime}>{formatClockTime(item.start_time)}</p>
        <p className={classes.n8nMeta}>{formatRelativeSince(item.start_time)}</p>
      </div>

      <span className={`${classes.n8nStatusPill} ${getPillStatusClass(item.status_code)}`}>
                {String(item.status_code || '-')}
            </span>

    </div>
  );

  const renderLastSeenFocusRow = (item, index) => (
    <div key={item.run_id || index} className={classes.n8nFocusDataRow}>
      <p className={classes.n8nFocusCellJob}>{(item.job || translate('n8n', 'unknown_label')).toUpperCase()}</p>
      <p className={classes.n8nFocusCellRunId}>{item.run_id || '-'}</p>
      <p className={classes.n8nFocusCellTime}>{formatClockTime(item.start_time) || '-'}</p>
      <span className={`${classes.n8nStatusPill} ${getPillStatusClass(item.status_code)}`}>
                {String(item.status_code || '-')}
            </span>
    </div>
  );


  return (
    <div className={classes.n8nDashboard}>
      <Header isN8nOpen refreshN8n={refreshN8n} lastUpdateTime={lastUpdateTime}/>
      <ContentContainer>
        <Info
          title={translate("n8n", "open_runs_title")}
          subtitle={translate("n8n", "open_runs_sub_title")}
          Icon={PlayCircleOutlineIcon}
          handleClick={() => setOpenRunsExpanded(!openRunsExpanded)}
          openRunsExpanded={openRunsExpanded}
          counter={openRunsCount}
        >
          <OpenRuns data={n8nSections.openRuns} openRunsExpanded={openRunsExpanded}/>
        </Info>
        <Scheduled 
        lastEventsPerJob={n8nSections.lastEventPerJob}  
        eventsToday={n8nSections.eventsToday} 
        openRuns={n8nSections.openRuns}
        />
      </ContentContainer>
    </div>
  );
};

N8nDashboard.propTypes = {
  classes: PropTypes.object.isRequired,
  n8nSections: PropTypes.shape({
    eventsToday: PropTypes.array,
    lastEventPerJob: PropTypes.array,
    openRuns: PropTypes.array,
  }).isRequired,
  failedJobs: PropTypes.array.isRequired,
  latestFailure: PropTypes.object,
  latestOpenRun: PropTypes.object,
  openHasMore: PropTypes.bool.isRequired,
  failedHasMore: PropTypes.bool.isRequired,
  openNextCursor: PropTypes.string,
  failedNextCursor: PropTypes.string,
  lastUpdateTime: PropTypes.number.isRequired,
  refreshN8n: PropTypes.func.isRequired
};

N8nDashboard.defaultProps = {
  latestFailure: null,
  latestOpenRun: null,
  openNextCursor: null,
  failedNextCursor: null,
};

export default N8nDashboard;
